import { forwardRef } from "react";
import PropTypes from "prop-types";
import { accentColorLink, grey3, spacing32, white, whiteTransparency40, whiteTransparency60, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import styled from "styled-components";
import shared from "./shared";
import { AppearanceInvert } from "@churchofjesuschrist/eden-appearance";
import { jsx as _jsx } from "react/jsx-runtime";
const iconSpecificFontSize = "1.5rem";
const [buttonIconTextDef, buttonIconText] = setAppearanceConstantLocal("button-icon-text", {
  light: "currentColor",
  dark: white,
  prefix: "eden"
});
const [buttonIconHoverDef, buttonIconHover] = setAppearanceConstantLocal("button-icon-hover", {
  light: adjustHexAlpha(grey3, 0.6),
  dark: whiteTransparency60,
  prefix: "eden"
});
const [buttonIconActiveDef, buttonIconActive] = setAppearanceConstantLocal("button-icon-active-background", {
  light: grey3,
  dark: whiteTransparency40,
  prefix: "eden"
});
const Component = styled.button.withConfig({
  componentId: "sc-1g7hsbc-0"
})(["", ";", ";", ";", ";border:none;position:relative;&:before{content:\"\";display:block;width:100%;height:100%;position:absolute;}border-radius:50%;", ";font-size:", ";padding:0;background:transparent;color:", ";@media (hover:hover){&:hover{background:", ";color:", ";}}&:active{background:", ";}&[aria-pressed=\"true\"]{color:", ";}&[disabled],&[disabled]:hover{background:transparent;}&:is(a):visited:not(:active){color:", ";}"], shared, buttonIconTextDef, buttonIconHoverDef, buttonIconActiveDef, _ref => {
  let {
    size
  } = _ref;
  return size === "large" ? `min-width: 3.5rem;
            min-height: 3.5rem;` : size === "small" ? `min-width: ${spacing32};
              min-height: ${spacing32};` : `min-width: 2.5rem;
        min-height: 2.5rem;`;
}, iconSpecificFontSize, buttonIconText, buttonIconHover, buttonIconText, buttonIconActive, accentColorLink, buttonIconText);
const Icon = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    size = "normal",
    href,
    disabled,
    renderAs = disabled ? "button" : href ? "a" : "button",
    type = renderAs === "button" ? "button" : undefined,
    ...props
  } = _ref2;
  return /*#__PURE__*/_jsx(AppearanceInvert, {
    inverted: props.inverted,
    children: /*#__PURE__*/_jsx(Component, {
      ...props,
      size: size,
      ref: ref,
      type: type,
      href: href,
      disabled: disabled,
      as: renderAs,
      children: props.children
    })
  });
});
Icon.displayName = "Icon";
Icon.propTypes = {
  /** The icon */
  children: PropTypes.node.isRequired,
  /** When present, the button becomes a "toggle" button. The value indicates whether it is toggled on or not. */
  "aria-pressed": PropTypes.oneOf([true, false]),
  /** Defines a value that labels the button. If another element is visible in your UI that already labels the
   * purpose of the button, use the 'aria-labelledby' prop instead. This prop is required if `aria-labelledby` is not used. */
  "aria-label": PropTypes.string,
  /** Identifies the element(s) that label(s) the button. Must be a space-separated list of one or more `id` values referencing
   * the element(s) present in the UI that label(s) the button. If no accompanying visible label(s) exist, you must instead use
   * the 'aria-label' prop instead. */
  "aria-labelledby": PropTypes.string,
  /** Whether to apply styles that help the button stand out on a dark background. (deprecated) */
  inverted: PropTypes.bool,
  /** The size of the button */
  size: PropTypes.oneOf(["small", "normal", "large"]),
  /** The [type of the button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Attributes) or the [type of the link](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Attributes). */
  type: PropTypes.string,
  /** If the button should be disabled */
  disabled: PropTypes.bool,
  /** The link that the button should navigate to on click */
  href: PropTypes.string,
  /** The HTML tag string or React component to render as.
   *
   * By default, if `href` is present and it is not `disabled`, it will render as an `<a>`. Otherwise it will default to `<button>`. */
  renderAs: PropTypes.elementType
};
export default Icon;